<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import Input from "../Inputs/Input.svelte";
  import CheckboxInput from "../Inputs/Checkbox.svelte";
  import Button from "../Button.svelte";
  import {
    CustomFieldsHelper,
    FullDataValidator,
    getPhoneMask,
    BackbuttonHelper,
    checkIsEntryTokenRequired,
    initialFieldValidation,
    getCustomCopy
  } from "../../lib/helpers/userDetails";
  import LoadingScreen from "../LoadingScreen.svelte";
  import { userStore, setRanInitialValidation, type UserStore } from "../../lib/stores/user";
  import { settingsStore, type CustomField } from "../../lib/stores/settings";
  import Typography from "../Typography.svelte";
  import { TypographyType } from "../../lib/constants/typographyMap";
  import CustomHtml from "../CustomHTML.svelte";
  import { type Route } from "../../lib/stores/session";
  import { onMount } from "svelte";
  import Youtube from "../Youtube.svelte";
  import Dob from "../Inputs/Dob.svelte";

  export let currentRoute;

  let isLoading = true;
  let isBusy = false;
  let fieldMessages: Record<string, string> = {};
  let invalidCustomFields: Record<string, "missing" | "invalid" | undefined> = {};
  let showBackButton: boolean = true;

  onMount(() => {
    showBackButton = BackbuttonHelper.showBackButton($settingsStore.v2 as { flow: Route[] });
  });

  const runValidations = () => {
    if (ranInitialValidation) {
      const { fieldValidations, customFieldsValidations } = FullDataValidator.validateAllData(copy, customFields, {
        disablePhone
      });
      fieldMessages = fieldValidations?.issues || {};
      invalidCustomFields = customFieldsValidations?.issues || {};
    }
  };

  const onNext = async () => {
    isBusy = true;

    const { fieldValidations, customFieldsValidations } = await FullDataValidator.validateAndSubmit(
      copy,
      customFields,
      {
        disablePhone
      }
    );

    setRanInitialValidation(true);
    fieldMessages = fieldValidations?.issues || {};
    invalidCustomFields = customFieldsValidations?.issues || {};

    isBusy = false;
  };

  const validate = (userStore: UserStore, customFields: CustomField[]) => {
    if (userStore.ranInitialValidation) {
      return Object.values(fieldMessages).every((message) => !message);
    }
    return initialFieldValidation(userStore, disablePhone, customFields);
  };

  $: copy = $getCopy("user") as Record<string, string>;

  let customFields: CustomField[] = [];
  // Excluding tax_id from custom fields, because that's going to show up in the address step
  $: allCustomFields = $settingsStore.custom_fields ?? [];
  $: customFields = allCustomFields.filter((field) => field.name !== "tax_id");

  $: ranInitialValidation = $userStore.ranInitialValidation;
  $: $userStore, runValidations();
  $: isValid = validate($userStore, customFields);
  $: CustomFieldsHelper.setupCustomFields(allCustomFields);
  $: {
    if ($userStore.hasLoaded) {
      isLoading = false;
    }
  }
  $: disablePhone = copy.disablePhone as unknown as boolean;
  $: isEntryTokenRequired = checkIsEntryTokenRequired();
  $: hasInvalidCustomFields = Object.values(invalidCustomFields).some((issue) => issue !== undefined);
</script>

<BaseStep {currentRoute} {showBackButton}>
  <svelte:fragment slot="content">
    {#if isLoading}
      <LoadingScreen />
    {:else}
      <div class="w-full">
        <div class="flex flex-col w-full mx-auto text-center lg:text-left">
          <SharedHeading headerCopy={copy.headerCopy} subHeaderCopy={copy.subHeaderCopy} />

          <CustomHtml customHtml={copy.customHtml} />

          <div class="flex flex-col mb-4 lg:mb-8 mt-4 lg:mt-8 gap-4">
            <Input
              bind:value={$userStore.user.first_name}
              placeholder={copy.firstNamePlaceholder}
              invalidMessage={fieldMessages.first_name} />
            <Input
              bind:value={$userStore.user.last_name}
              placeholder={copy.lastNamePlaceholder}
              invalidMessage={fieldMessages.last_name} />
            <Input
              bind:value={$userStore.user.email}
              placeholder={copy.emailPlaceholder}
              invalidMessage={fieldMessages.email}
              type="email" />
            {#if !disablePhone}
              <Input
                bind:value={$userStore.user.phone}
                placeholder={copy.phonePlaceholder}
                invalidMessage={fieldMessages.phone}
                mask={getPhoneMask()}
                type="tel" />
            {/if}
            {#each customFields as customField}
              {#if customField.type === "text_input"}
                <Input
                  bind:value={$userStore.customFields[customField.name]}
                  placeholder={copy[`customField-${customField.identifier}Copy`] ?? customField.name}
                  invalidMessage={getCustomCopy(customField, invalidCustomFields[customField.name], copy)} />
              {:else if customField.type === "date_input"}
                <Dob
                  bind:value={$userStore.customFields[customField.name]}
                  {customField}
                  {copy}
                  {invalidCustomFields} />
              {:else}
                <CheckboxInput
                  bind:value={$userStore.customFields[customField.name]}
                  label={copy[`customField-${customField.identifier}Copy`] ?? customField.name}
                  invalidMessage={getCustomCopy(customField, invalidCustomFields[customField.name], copy)} />
              {/if}
            {/each}
            {#if isEntryTokenRequired}
              <Input
                bind:value={$userStore.user.entryToken}
                placeholder={copy.entryTokenPlaceholder}
                invalidMessage={fieldMessages.entryToken} />
            {/if}
          </div>
          {#if copy.tertiaryCopy && copy.tertiaryCopy.trim().length}
            <div>
              <Typography mobile={TypographyType.Body3Regular} desktop={TypographyType.Body3Regular}>
                {#if copy.preTertiaryCopy && copy.preTertiaryCopy.trim().length}{copy.preTertiaryCopy} "{copy.buttonText}"
                {/if}
                {copy.tertiaryCopy}
              </Typography>
            </div>
          {/if}
          <div class="lg:mt-0 flex flex-col lg:block mt-4 lg:mt-10">
            <Button
              primary
              --button-padding="0.5rem 3.5rem"
              disabled={!isValid || isBusy || hasInvalidCustomFields}
              {isBusy}
              on:click={onNext}>
              {copy.buttonText}
            </Button>
          </div>

          <Youtube videoId={copy.videoId} />
        </div>
      </div>
    {/if}
  </svelte:fragment>
</BaseStep>
